import React, { useState, useEffect, useCallback } from 'react';
import { taskLists } from '../../api';
import TaskListView from './TaskListView';
import './Tasks.css';

const REFRESH_MS = 30 * 1000;
const MAX_TASKS = 30;
const FORMATS = [
  { key: 'numbered', label: '1. 2. 3.' },
  { key: 'bullets', label: '• • •' },
  { key: 'plain', label: 'Plain' }
];

const when = (d) => new Date(d).toLocaleString([], { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

// Headcoach: write a task list (any number of tasks, numbered / bulleted / plain), send it to one or more coaches,
// and see their tick / cross / question-mark answers.
const HeadcoachTasksCard = () => {
  const [coaches, setCoaches] = useState(null);
  const [selected, setSelected] = useState([]); // coach ids to send to
  const [viewCoachId, setViewCoachId] = useState('');
  const [sent, setSent] = useState([]);
  const [title, setTitle] = useState('');
  const [format, setFormat] = useState('numbered');
  const [tasks, setTasks] = useState(['']);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadCoaches = useCallback(() => {
    return taskLists.getCoaches()
      .then(res => {
        const list = res.data.coaches || [];
        setCoaches(list);
        setViewCoachId(prev => prev || (list[0] && list[0]._id) || '');
      })
      .catch(() => { setCoaches([]); setError('Could not load your coaches'); });
  }, []);

  const loadSent = useCallback(() => {
    if (!viewCoachId) return Promise.resolve();
    return taskLists.getSent(viewCoachId).then(res => setSent(res.data.lists || [])).catch(() => {});
  }, [viewCoachId]);

  useEffect(() => { loadCoaches(); }, [loadCoaches]);

  useEffect(() => {
    setSent([]);
    loadSent();
    const timer = setInterval(() => { if (!document.hidden) { loadSent(); loadCoaches(); } }, REFRESH_MS);
    return () => clearInterval(timer);
  }, [loadSent, loadCoaches]);

  const toggleCoach = (id) => setSelected(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]));
  const setTask = (i, value) => setTasks(prev => prev.map((t, idx) => (idx === i ? value : t)));
  const addTask = () => setTasks(prev => (prev.length < MAX_TASKS ? [...prev, ''] : prev));
  const removeTask = (i) => setTasks(prev => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : ['']));

  const filled = tasks.map(t => t.trim()).filter(Boolean);
  const recipients = selected.length > 0 ? selected : (coaches && coaches.length === 1 ? [coaches[0]._id] : []);

  const handleSend = async (e) => {
    e.preventDefault();
    if (sending || filled.length === 0 || recipients.length === 0) return;
    setSending(true);
    setError('');
    setNotice('');
    try {
      await taskLists.send({ coachIds: recipients, title: title.trim(), format, tasks: filled });
      setTitle('');
      setTasks(['']);
      setNotice(recipients.length === 1 ? 'Sent' : `Sent to ${recipients.length} coaches`);
      if (recipients.length === 1) setViewCoachId(recipients[0]);
      loadSent();
      loadCoaches();
    } catch (err) {
      setError(err?.response?.data?.error || 'Could not send the task list');
    } finally {
      setSending(false);
    }
  };

  const mark = (i) => (format === 'numbered' ? `${i + 1}.` : format === 'bullets' ? '•' : '');

  return (
    <div className="settings-section tl-form">
      <label>Task Lists for Coaches</label>
      {coaches && coaches.length === 0 && !error && <p className="password-info">No coaches are assigned to you yet.</p>}
      {error && <p className="tl-error">{error}</p>}

      {coaches && coaches.length > 0 && (
        <form onSubmit={handleSend}>
          <label className="tl-label">Send to</label>
          <div className="tl-coaches">
            {coaches.map(c => (
              <label key={c._id} className="tl-coach">
                <input
                  type="checkbox"
                  checked={selected.includes(c._id) || (selected.length === 0 && coaches.length === 1)}
                  onChange={() => toggleCoach(c._id)}
                />
                <span>{c.name}</span>
              </label>
            ))}
          </div>

          <label className="tl-label">Title (optional)</label>
          <input className="tl-input" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Before Friday" />

          <label className="tl-label">Format</label>
          <div className="tl-seg">
            {FORMATS.map(f => (
              <button key={f.key} type="button" aria-pressed={format === f.key} onClick={() => setFormat(f.key)}>{f.label}</button>
            ))}
          </div>

          <label className="tl-label">Tasks ({filled.length})</label>
          {tasks.map((t, i) => (
            <div key={i} className="tl-task-row">
              {mark(i) && <span className="tl-mark">{mark(i)}</span>}
              <input className="tl-input" value={t} maxLength={300} onChange={(e) => setTask(i, e.target.value)} placeholder="Write a task" />
              <button type="button" className="tl-x" onClick={() => removeTask(i)} aria-label="Remove this task">✕</button>
            </div>
          ))}
          <button type="button" className="cancel-button" onClick={addTask} disabled={tasks.length >= MAX_TASKS} style={{ width: '100%', marginBottom: '12px' }}>
            + Add a task
          </button>

          <button type="submit" className="submit-button" disabled={sending || filled.length === 0 || recipients.length === 0} style={{ width: '100%' }}>
            {sending ? 'Sending...' : 'Send task list'}
          </button>
          {notice && <p className="tl-note" style={{ marginTop: '8px' }}>{notice}</p>}

          <label className="tl-label" style={{ marginTop: '20px' }}>Answers</label>
          <select value={viewCoachId} onChange={(e) => setViewCoachId(e.target.value)} className="tl-input" style={{ marginBottom: '10px' }}>
            {coaches.map(c => (
              <option key={c._id} value={c._id}>{c.name}{c.waiting ? ` (${c.waiting} open)` : ''}</option>
            ))}
          </select>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {sent.length === 0 ? (
              <p className="tl-note">Nothing sent to this coach yet.</p>
            ) : sent.map(l => (
              <div key={l._id} className={`tl-card${l.items.every(i => i.response) ? ' done' : ''}`}>
                <div className="tl-head">
                  <h4 className="tl-title">{l.title || 'Task list'}</h4>
                  <span className="tl-meta">{when(l.createdAt)}</span>
                </div>
                <TaskListView list={l} mode="head" />
              </div>
            ))}
          </div>
        </form>
      )}
    </div>
  );
};

export default HeadcoachTasksCard;
